import React, { useMemo } from 'react';
import { ModelTimestamps } from '@shared/models/base.model';
import { DateTime, Info, Settings } from 'luxon';
import { Tooltip, Typography, TypographyProps } from '@mui/material';
import { getTimeSinceEvent } from '@shared/helpers/date.helpers';
import { mergeSx } from '@client/helpers/mui.helpers';

Settings.defaultWeekSettings = {
  // Sunday = 7, Monday = 1.
  firstDay: 1,
  // Makes sure we don't lose the other information from `defaultWeekSettings`
  minimalDays: Info.getMinimumDaysInFirstWeek(),
  weekend: Info.getWeekendWeekdays(),
};

type DateValue = Date | ModelTimestamps | string | null;

const getDateTime = (date: DateValue): DateTime | undefined => {
  if (!date) return undefined;
  if (typeof date === 'string') return DateTime.fromISO(date);
  if (date instanceof Date) return DateTime.fromJSDate(date);
  if ('created_at' in date) return DateTime.fromISO(date.created_at);
  throw new Error('Invalid date format');
};

type RelativeFormat = { type: 'RELATIVE'; dateFormat?: string };
type StringFormat = { type: 'CUSTOM'; format: string };
type DateTimeFormat = {
  type: 'DATE_TIME_OPTIONS';
  format: Intl.DateTimeFormatOptions;
};
type FormatOptions = RelativeFormat | StringFormat | DateTimeFormat;

const defaultFormatOption: FormatOptions = {
  type: 'DATE_TIME_OPTIONS',
  format: DateTime.DATETIME_MED,
};

export const formatDate = (
  date: DateValue,
  options: FormatOptions = defaultFormatOption,
): string | undefined => {
  const dateTime = getDateTime(date);
  if (!dateTime) return undefined;
  switch (options.type) {
    case 'RELATIVE':
      return getTimeSinceEvent(dateTime, options.dateFormat);
    case 'CUSTOM':
      return dateTime.toFormat(options.format);
    case 'DATE_TIME_OPTIONS':
      return dateTime.toLocaleString(options.format);
  }
};

type DateTextProps = Omit<TypographyProps, 'variant' | 'pl'> & {
  date?: DateValue;
  tooltipPrefix?: string;
  format?: FormatOptions;
  variant?: TypographyProps['variant'];
  pl?: number;
};

const DateText: React.FC<DateTextProps> = ({
  date = null,
  tooltipPrefix,
  format = defaultFormatOption,
  variant = 'inherit',
  pl,
  sx,
  ...typographyProps
}) => {
  const dateTime = useMemo(() => getDateTime(date), [date]);

  const tooltipDate = dateTime?.toFormat('dd MMM yyyy, HH:mm');

  if (!dateTime) return null;
  const dateComponent = (
    <Typography variant={variant} {...typographyProps} sx={mergeSx({ pl }, sx)}>
      {formatDate(date, format)}
    </Typography>
  );

  if (tooltipPrefix) {
    return (
      <Tooltip title={`${tooltipPrefix} ${tooltipDate}`}>
        {dateComponent}
      </Tooltip>
    );
  }

  return dateComponent;
};

export default DateText;
